import { BaseWebComponentView } from '../../framework/BaseWebComponentView';
import { Collections, PhasrApplyActionSchema } from '../types';
import { htmlEncode } from '../../utils/escape.utils';
import { FontSizes, FontWeights, TemplateState } from '../../types/common/types';
import { GzButtonWebComponent } from '../../ui/gzButton';
import { translateFn } from '../../utils/translator.helper';
import { GzCollapsableContainerWebComponent } from '../../ui/gzCollapsableContainer';
import { GzLabeledContentWebComponent } from '../../ui/gzLabeledContent';
import { toggleVisibility } from '../../utils/htmlElement.utils';
import { PhasrBehavioralProfilesComponent } from '../../phasrRecommendations/phasrBehavioralProfilesComponent';
import { WEB_COMPONENT_PHASR_BEHAVIORAL_PROFILES_RESTRICT } from '../../webcomponents.constants';
import { BehavioralProfileTypes } from '../../phasrRecommendations/phasrBehavioralProfilesComponent/types';
import { RecommendationType } from '../../phasrRecommendations/phasrRecommendationsGrid/types';

export class PhasrApplyActionPanelView extends BaseWebComponentView {
  private buttonStrings = {
    showMore: translateFn('Show more'),
    showLess: translateFn('Show less'),
  };

  public dom: {
    $wrapper: HTMLElement;
    $description: GzCollapsableContainerWebComponent;
    $descriptionText: HTMLElement;
    $applyButton: GzButtonWebComponent;
    $cancelButton: GzButtonWebComponent;
    $targetedActivityType: GzLabeledContentWebComponent;
    $attackSurfaceReduction: GzLabeledContentWebComponent;
    $monitoredRule: GzLabeledContentWebComponent;
    $ruleTriggers: GzLabeledContentWebComponent;
    $behavioralProfiles: PhasrBehavioralProfilesComponent;
  }

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$description = this.shadow.querySelector('#description-container');
    this.dom.$descriptionText = this.shadow.querySelector('#description-text');
    this.dom.$applyButton = this.shadow.querySelector('#apply-button');
    this.dom.$cancelButton = this.shadow.querySelector('#cancel-button');
    this.dom.$targetedActivityType = this.shadow.querySelector('#targeted-activity-type');
    this.dom.$attackSurfaceReduction = this.shadow.querySelector('#attack-surface-reduction');
    this.dom.$monitoredRule = this.shadow.querySelector('#rule-info');
    this.dom.$ruleTriggers = this.shadow.querySelector('#rule-triggers');
    this.dom.$behavioralProfiles = this.shadow.querySelector(WEB_COMPONENT_PHASR_BEHAVIORAL_PROFILES_RESTRICT) as PhasrBehavioralProfilesComponent;

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$targetedActivityType.label = translateFn('Targeted activity type');
    this.dom.$attackSurfaceReduction.label = translateFn('Attack surface reduction');
    this.dom.$cancelButton.innerText = translateFn('Cancel');
  }

  private setupDescription(): void {
    this.dom.$description.fixedContentHeight = 42; //represents the height of the textarea. depends on the fonts and line height. if too small, the text will be cut off
    this.dom.$description.buttonStrings = this.buttonStrings;
    this.dom.$description.buttonSettings = {
      customClass: 'phasr-description-button',
      type: 'quiet',
      color: 'primary',
      buttonHeight: 'small',
      buttonFontSize: 'medium',
      theme: 'light',
      lowercase: true,
    }
  }

  async buildSuccessView(data: PhasrApplyActionSchema) {
    toggleVisibility(this.dom.$attackSurfaceReduction, data.collection === Collections.RECOMMENDATIONS);
    toggleVisibility(this.dom.$monitoredRule, data.collection === Collections.RECOMMENDATIONS);
    toggleVisibility(this.dom.$ruleTriggers, data.collection === Collections.RULES);
    this.populateFields(data);
    this.setupDescription();
    await this.buildBehavioralProfilesView(data);
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private populateFields(data: PhasrApplyActionSchema): void {
    this.dom.$monitoredRule.label = translateFn('Monitored rule');
    this.dom.$ruleTriggers.label = translateFn('Rule triggers');
    this.dom.$applyButton.innerText = data.collection === Collections.RECOMMENDATIONS ? translateFn('Apply') : translateFn('Edit Access');

    if (data.collection === Collections.RECOMMENDATIONS) {
      const reductionPotential = data.reductionPotential  ?? 0;
      let percentageText = `${reductionPotential}%`;
      if (reductionPotential !== 0) {
        percentageText = `-${percentageText}`;
      }
      const attackSurfaceReduction = (data.action ?? RecommendationType.RESTRICT_ACCESS) === RecommendationType.RESTRICT_ACCESS ? percentageText : '-';
      const attackSurfaceReductionStyle = data.action === RecommendationType.RESTRICT_ACCESS ? 'label-percentage' : '';
      this.dom.$attackSurfaceReduction.innerHTML = `<gz-text class="${attackSurfaceReductionStyle}" fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.NORMAL}">${attackSurfaceReduction}</gz-text>`;
    }
    this.dom.$descriptionText.innerHTML = htmlEncode(data.description);
    this.dom.$targetedActivityType.innerHTML = `<gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.NORMAL}">${data.targetedActivityType}</gz-text>`;
    this.dom.$monitoredRule.innerHTML = `<gz-link class='cell-label' type="quiet" color="primary"">${data.rule}</gz-link>`;
    this.dom.$ruleTriggers.innerHTML = `<gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.NORMAL}">${data.rule === 0 ? '-' : data.rule}</gz-text>`;
  }

  private async buildBehavioralProfilesView(data: PhasrApplyActionSchema): Promise<void> {
    this.dom.$behavioralProfiles.injectDependencies(this.dependencies);
    this.dom.$behavioralProfiles.setGridData({
      companyId: data.companyId,
      id: data.id,
      behavioralProfileType: BehavioralProfileTypes.AUTOPILOT,
    });
    this.dom.$behavioralProfiles.collection = data.collection ?? '';
    await this.dom.$behavioralProfiles.start();
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }
}
